import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, updateProfile } from 'firebase/auth'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { auth } from '@/frontend/lib/firebase'
import { useAuth } from '@/frontend/contexts/AuthContext'

/**
 * Sign-up page component with Firebase authentication
 * Supports email/password and Google sign-up
 */
export default function SignUpPage() {
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		email: '',
		password: '',
		confirmPassword: ''
	})
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const navigate = useNavigate()
	const { isSignedIn, signInWithLine, getLineAuthError } = useAuth()

	// Redirect if already signed in
	if (isSignedIn) {
		navigate('/', { replace: true })
		return null
	}

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value
		})
	}

	const handleEmailSignUp = async (e: React.FormEvent) => {
		e.preventDefault()
		setLoading(true)
		setError('')

		// Validate form
		if (formData.password !== formData.confirmPassword) {
			setError('Passwords do not match.')
			setLoading(false)
			return
		}

		if (formData.password.length < 6) {
			setError('Password must be at least 6 characters long.')
			setLoading(false)
			return
		}

		try {
			const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password)
			
			// Update user profile with name
			const displayName = `${formData.firstName} ${formData.lastName}`.trim()
			if (displayName) {
				await updateProfile(userCredential.user, { displayName })
			}

			console.log('[Auth] Email sign-up successful')
			navigate('/', { replace: true })
		} catch (error: any) {
			console.error('[Auth] Email sign-up error:', error)
			setError(getErrorMessage(error.code))
		} finally {
			setLoading(false)
		}
	}

	const handleGoogleSignUp = async () => {
		setLoading(true)
		setError('')

		try {
			const provider = new GoogleAuthProvider()
			await signInWithPopup(auth, provider)
			console.log('[Auth] Google sign-up successful')
			navigate('/', { replace: true })
		} catch (error: any) {
			console.error('[Auth] Google sign-up error:', error)
			setError(getErrorMessage(error.code))
		} finally {
			setLoading(false)
		}
	}

	const handleLineSignUp = async () => {
		setLoading(true)
		setError('')

		try {
			await signInWithLine()
			console.log('[Auth] LINE sign-up initiated')
		} catch (error: any) {
			console.error('[Auth] LINE sign-up error:', error)
			setError(getLineAuthError(error))
			setLoading(false)
		}
	}

	const getErrorMessage = (errorCode: string) => {
		switch (errorCode) {
			case 'auth/email-already-in-use':
				return 'An account with this email already exists. Please sign in instead.'
			case 'auth/invalid-email':
				return 'Please enter a valid email address.'
			case 'auth/weak-password':
				return 'Password is too weak. Please choose a stronger password.'
			case 'auth/popup-closed-by-user':
				return 'Sign-up was cancelled. Please try again.'
			default:
				return 'An error occurred during sign-up. Please try again.'
		}
	}

	return (
		<div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 p-5">
			<Card className="w-full max-w-md shadow-2xl border-0 rounded-xl">
				<CardHeader className="text-center">
					<CardTitle className="text-2xl font-bold text-blue-600">
						Create Account
					</CardTitle>
					<CardDescription className="text-gray-600">
						Join the Thai Court Document Processing System
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{error && (
						<Alert variant="destructive">
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}

					<form onSubmit={handleEmailSignUp} className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="firstName">First Name</Label>
								<Input
									id="firstName"
									name="firstName"
									type="text"
									placeholder="John"
									value={formData.firstName}
									onChange={handleInputChange}
									disabled={loading}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="lastName">Last Name</Label>
								<Input
									id="lastName"
									name="lastName"
									type="text"
									placeholder="Doe"
									value={formData.lastName}
									onChange={handleInputChange}
									disabled={loading}
								/>
							</div>
						</div>

						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								name="email"
								type="email"
								placeholder="john@example.com"
								value={formData.email}
								onChange={handleInputChange}
								required
								disabled={loading}
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<div className="relative">
								<Input
									id="password"
									name="password"
									type={showPassword ? 'text' : 'password'}
									placeholder="Create a password"
									value={formData.password}
									onChange={handleInputChange}
									required
									disabled={loading}
								/>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
									onClick={() => setShowPassword(!showPassword)}
									disabled={loading}
								>
									<FontAwesomeIcon
										icon={showPassword ? faEyeSlash : faEye}
										className="h-4 w-4 text-gray-400"
									/>
								</Button>
							</div>
						</div>

						<div className="space-y-2">
							<Label htmlFor="confirmPassword">Confirm Password</Label>
							<div className="relative">
								<Input
									id="confirmPassword"
									name="confirmPassword"
									type={showConfirmPassword ? 'text' : 'password'}
									placeholder="Confirm your password"
									value={formData.confirmPassword}
									onChange={handleInputChange}
									required
									disabled={loading}
								/>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
									onClick={() => setShowConfirmPassword(!showConfirmPassword)}
									disabled={loading}
								>
									<FontAwesomeIcon
										icon={showConfirmPassword ? faEyeSlash : faEye}
										className="h-4 w-4 text-gray-400"
									/>
								</Button>
							</div>
						</div>

						<Button type="submit" className="w-full" disabled={loading}>
							{loading ? 'Creating account...' : 'Create Account'}
						</Button>
					</form>

					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<span className="w-full border-t" />
						</div>
						<div className="relative flex justify-center text-xs uppercase">
							<span className="bg-background px-2 text-muted-foreground">Or continue with</span>
						</div>
					</div>

					<Button
						type="button"
						variant="outline"
						className="w-full"
						onClick={handleGoogleSignUp}
						disabled={loading}
					>
						{loading ? 'Creating account...' : 'Sign up with Google'}
					</Button>

					<Button
						type="button"
						variant="outline"
						className="w-full bg-green-50 border-green-200 hover:bg-green-100 text-green-700"
						onClick={handleLineSignUp}
						disabled={loading}
					>
						{loading ? 'Creating account...' : 'Sign up with LINE'}
					</Button>

					<div className="text-center text-sm text-gray-600">
						Already have an account?{' '}
						<Button
							variant="link"
							className="p-0 text-blue-600 hover:text-blue-800"
							onClick={() => navigate('/sign-in')}
							disabled={loading}
						>
							Sign in
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}
